import {DateTime} from "luxon";
import {Interval} from "../../../pages/Insights/View/VisitorDensity";
import { VisitorDensityInterval } from "./VisitorsDensityTimelineMap.types";
import {useEffect, useRef, useState} from "react";
import {useOperator} from "../../../context/OperatorContext";
import {getIntervals} from "./VisitorsDensityTimelineMap.helpers";

// Constants ----

const playbackSpeeds = [3000, 2000, 1000, 500] as const;

// Types ----

export type PlaybackSpeed = (typeof playbackSpeeds)[number];

type VisitorDensityTimeline = {
  currentInterval: VisitorDensityInterval | null
  startPlayback: () => void
  stopPlayback: () => void
  pausePlayback: () => void
  skipBackward: () => void
  skipForward: () => void
  cyclePlaybackSpeed: () => void
  playbackSpeed: PlaybackSpeed
  isPlaying?: boolean
  isStart?: boolean
  isEnd?: boolean
}

type Params = {
  since?: DateTime
  until?: DateTime
  interval?: Interval
}

// Custom hook ----

export function useVisitorDensityPlayback({since, until, interval = '30-mins'}: Params): VisitorDensityTimeline {
  const defaultValues: VisitorDensityTimeline = {
    currentInterval: null,
    startPlayback: () => {},
    stopPlayback: () => {},
    pausePlayback: () => {},
    skipBackward: () => {},
    skipForward: () => {},
    cyclePlaybackSpeed: () => {},
    playbackSpeed: 2000,
    isStart: true,
    isEnd: false,
  };

  const [currentInterval, setCurrentInterval] = useState<VisitorDensityInterval | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(defaultValues.playbackSpeed);

  const { settings } = useOperator();
  const locale = settings?.localisation.locale ?? "en-GB";

  // Keep track of the current interval and playback state
  const requestAnimationFrameRef = useRef<number | null>(null);
  const lastStepTimeRef = useRef<number | null>(null);
  const intervalsRef = useRef<VisitorDensityInterval[]>([]);
  const currentIntervalIndexRef = useRef(0);
  const isPlayingRef = useRef(false);
  const playbackSpeedRef = useRef(playbackSpeed);

  // Derive isStart and isEnd booleans
  const isStart = currentIntervalIndexRef.current === 0;
  const isEnd = currentIntervalIndexRef.current === intervalsRef.current.length - 1;

  useEffect(() => {
    if (!since || !until) return;

    resetPlayback();
    const intervals = getIntervals({ since, until, locale, interval }) ?? [];
    intervalsRef.current = intervals;
    setCurrentInterval(intervals?.[0] ?? null);
  }, [since, until, locale, interval]);

  // Bail early if we don't have dates
  if (!since || !until) return defaultValues;

  // Step function to pass to requestAnimationFrame
  function step(timestamp: number) {
    if (!isPlayingRef.current) return;

    if (lastStepTimeRef.current === null) {
      lastStepTimeRef.current = timestamp;
    }

    const intervals = intervalsRef.current;
    const elapsed = timestamp - lastStepTimeRef.current;

    if (currentIntervalIndexRef.current >= intervals.length - 1) {
      pausePlayback();
      return;
    }

    if (elapsed >= playbackSpeedRef.current) {
      currentIntervalIndexRef.current++;
      setCurrentInterval(intervals[currentIntervalIndexRef.current]);
      lastStepTimeRef.current = timestamp;
    }

    requestAnimationFrameRef.current = requestAnimationFrame(step);
  }

  function resetPlayback() {
    currentIntervalIndexRef.current = 0;
    setCurrentInterval(intervalsRef.current[0]);
    lastStepTimeRef.current = null;
    isPlayingRef.current = false;
    setIsPlaying(false);
  }

  function startPlayback() {
    if (isPlayingRef.current) return;

    setIsPlaying(true);
    isPlayingRef.current = true;
    lastStepTimeRef.current = null;
    requestAnimationFrameRef.current = requestAnimationFrame(step);
  }

  function pausePlayback() {
    isPlayingRef.current = false;
    setIsPlaying(false);

    if (requestAnimationFrameRef.current !== null) {
      cancelAnimationFrame(requestAnimationFrameRef.current);
      requestAnimationFrameRef.current = null;
    }
  }

  function stopPlayback() {
    pausePlayback();
    resetPlayback();
  }

  function skipForward() {
    const intervals = intervalsRef.current;
    const currentIntervalIndex = currentIntervalIndexRef.current;

    if (currentIntervalIndex >= intervals.length - 1) return;
    if (isPlayingRef.current) pausePlayback();

    const nextIntervalIndex = currentIntervalIndex + 1;
    currentIntervalIndexRef.current = nextIntervalIndex;

    setCurrentInterval(intervals?.[nextIntervalIndex] ?? null);
  }

  function skipBackward() {
    const intervals = intervalsRef.current;
    const currentIntervalIndex = currentIntervalIndexRef.current;

    if (currentIntervalIndex === 0) return;
    if (isPlayingRef.current) pausePlayback();

    const previousIntervalIndex = currentIntervalIndex - 1;
    currentIntervalIndexRef.current = previousIntervalIndex;

    setCurrentInterval(intervals?.[previousIntervalIndex] ?? null);
  }

  function cyclePlaybackSpeed() {
    const currentIndex = playbackSpeeds.indexOf(playbackSpeedRef.current);
    const nextIndex = (currentIndex + 1) % playbackSpeeds.length;
    const nextSpeed = playbackSpeeds[nextIndex];
    playbackSpeedRef.current = nextSpeed;
    setPlaybackSpeed(nextSpeed);
  }

  return {
    currentInterval,
    startPlayback,
    stopPlayback,
    pausePlayback,
    skipBackward,
    skipForward,
    cyclePlaybackSpeed,
    playbackSpeed,
    isPlaying,
    isStart,
    isEnd,
  };
}